/**
 * カレンダー週切り替え + 時刻選択肢の絞り込み
 * PC: 常に12日表示（コンテンツ幅を12等分）、1週間（7日）単位で切り替え
 * SP: 7日分の表示幅で、全日付を横スクロールして閲覧（ナビボタンは非表示）
 *
 * input版（日付ラジオ）では、選択した日付の data-slots（その日に選べる見学枠）に合わせて
 * 同一フォーム内の時刻選択肢（.c-forms-input-time-slot）を絞り込む。
 */

const BP_MD = 768; // @include md の閾値
const SP_VISIBLE = 7;
const PC_VISIBLE = 12;
const STEP_DAYS = 7; // 1週間単位でページ送り

class InputCalendar {
    constructor(root) {
        this.root    = root;
        this.cols    = Array.from(root.querySelectorAll('.c-forms-input-calendar-col'));
        this.btnPrev = root.querySelector('.js-calendar-prev');
        this.btnNext = root.querySelector('.js-calendar-next');

        // ブレークポイント監視（offset計算より先に設定）
        this.mq = window.matchMedia(`(max-width: ${BP_MD}px)`);
        this.mq.addEventListener('change', () => {
            this.offset = 0;
            this.render();
        });

        // チェック済み日付のページを初期オフセットにする
        const checkedIndex = this.cols.findIndex(
            (col) => col.querySelector('.c-forms-input-calendar-col__radio:checked')
        );
        this.offset = checkedIndex >= 0 ? Math.floor(checkedIndex / this.step()) : 0;

        this.btnPrev.addEventListener('click', () => {
            this.offset = Math.max(0, this.offset - 1);
            this.render();
        });

        this.btnNext.addEventListener('click', () => {
            this.offset = Math.min(this.maxOffset(), this.offset + 1);
            this.render();
        });

        this.setupSubmit();
        this.setupTimeSlotSync();

        this.render();
    }

    // 遷移ボタン: 選択中の日付を「name=Y-m-d」のクエリで付与して data-href へ遷移
    setupSubmit() {
        const wrap = this.root.querySelector('.js-calendar-submit');
        if (!wrap) return;

        const btn = wrap.querySelector('button');
        btn?.addEventListener('click', () => {
            const url     = new URL(wrap.dataset.href, window.location.origin);
            const checked = this.root.querySelector('.c-forms-input-calendar-col__radio:checked');
            if (checked) {
                url.searchParams.set(checked.name, checked.value);
            }
            window.location.href = url.toString();
        });
    }

    // 時刻選択肢の絞り込み: 選択した日付の data-slots に含まれない枠を非表示にする
    // △（見学リクエスト）の日は注記も表示する
    setupTimeSlotSync() {
        const form     = this.root.closest('form');
        const timeSlot = form?.querySelector('.c-forms-input-time-slot');
        if (!timeSlot) return;

        const items = Array.from(timeSlot.querySelectorAll('.c-forms-input-time-slot__item'));
        const note  = timeSlot.querySelector('.js-time-slot-note');

        const apply = (radio) => {
            const col = radio.closest('.c-forms-input-calendar-col');

            let slots = null;
            try {
                slots = JSON.parse(col?.dataset.slots ?? 'null');
            } catch {
                slots = null;
            }

            items.forEach((item) => {
                const input   = item.querySelector('input');
                // data-slots が無い日（旧データ等）は絞り込まない
                const allowed = !Array.isArray(slots) || slots.includes(input.value);

                item.toggleAttribute('data-hidden', !allowed);
                input.disabled = !allowed;
                // 選択済みの時刻が選べなくなった場合はクリアして選び直させる
                if (!allowed && input.checked) {
                    input.checked = false;
                }
            });

            note?.toggleAttribute('data-hidden', col?.dataset.status !== 'request');
        };

        this.root.querySelectorAll('.c-forms-input-calendar-col__radio').forEach((radio) => {
            radio.addEventListener('change', () => apply(radio));
        });

        // 初期表示: 選択済みの日付（クエリ・old値）があればその日の枠に絞る
        const checked = this.root.querySelector('.c-forms-input-calendar-col__radio:checked');
        if (checked) {
            apply(checked);
        }
    }

    // SP判定
    isSp() {
        return this.mq.matches;
    }

    // 表示する日数
    // SP: 7日分の表示幅（残りは横スクロールで閲覧）
    // PC: 常に12日（CSS 側で列幅をコンテンツ幅の12等分にする）
    visibleCount() {
        return this.isSp() ? SP_VISIBLE : PC_VISIBLE;
    }

    // 1回の切り替えで進む日数（常に1週間 = 7日）
    step() {
        return STEP_DAYS;
    }

    // 最大オフセット値
    maxOffset() {
        return Math.max(0, Math.ceil((this.cols.length - this.visibleCount()) / this.step()));
    }

    // 表示を更新する
    render() {
        // SP: 全日付を表示幅7日分のカラムとして横スクロールで閲覧（ナビは CSS で非表示）
        if (this.isSp()) {
            this.cols.forEach((col) => col.removeAttribute('data-hidden'));
            return;
        }

        const start = this.offset * this.step();
        const end   = start + this.visibleCount();

        this.cols.forEach((col, i) => {
            col.toggleAttribute('data-hidden', i < start || i >= end);
        });

        this.btnPrev.disabled = this.offset === 0;
        this.btnNext.disabled = this.offset >= this.maxOffset();
    }
}

const main = () => {
    document.querySelectorAll('.c-forms-input-calendar').forEach((root) => {
        new InputCalendar(root);
    });
};

export default main;
